// Client-side reference data translations. Falls back to backend labels when key missing.
export const REFDATA_EN = {
  objectives: {
    reduce_risk: "Reduce AD compromise risk",
    audit: "Prepare for an audit or certification",
    cyber_insurance: "Address a cyber-insurance request",
    monitor_admins: "Monitor admins and contractors",
    save_time: "Save time on investigations",
    detect_changes: "Detect critical changes",
    secure_transformation: "Secure a transformation project",
    copilot: "Prepare a Microsoft Copilot rollout",
    zero_trust: "Structure a Zero Trust initiative",
    incident: "Respond to a recent incident",
  },
  sectors: {
    "Services financiers / assurance": "Financial services / insurance",
    "Santé / médico-social": "Healthcare / social services",
    "Secteur public / collectivité": "Public sector / local government",
    "Industrie": "Manufacturing",
    "Énergie / utilities": "Energy / utilities",
    "Transport / logistique": "Transport / logistics",
    "Retail / distribution": "Retail / distribution",
    "Services professionnels": "Professional services",
    "Technologie / SaaS": "Technology / SaaS",
    "Éducation": "Education",
    "Construction / immobilier": "Construction / real estate",
    "Autre": "Other",
  },
  sizes: {
    s1: "under 50", s2: "50 to 249", s3: "250 to 999",
    s4: "1,000 to 4,999", s5: "5,000 to 19,999", s6: "20,000 and above",
  },
  criticity_levels: {
    c1: "Important internal support",
    c2: "Critical for most users",
    c3: "Critical for production or operations",
    c4: "Critical for multiple entities or countries",
    c5: "Vital or regulated infrastructure",
  },
  downtime_costs: {
    d1: "under €1,000", d2: "€1,000 – €5,000", d3: "€5,000 – €20,000",
    d4: "€20,000 – €100,000", d5: "over €100,000", d6: "I don't know",
  },
  deployment: {
    onprem: "AD on-premises only",
    hybrid: "Hybrid AD / Entra ID",
    cloud: "Cloud-first",
  },
  yes_no_unknown: { yes: "Yes", partial: "Partially", no: "No", unknown: "I don't know" },
  maturity_levels: {
    controlled: "Controlled", partial: "Partial", uncontrolled: "Uncontrolled", unknown: "Unknown",
  },
  monitoring_levels: {
    automated: "Automated & supervised", manual: "Available but manual",
    partial: "Partially available", absent: "Absent", unknown: "Unknown",
  },
  frameworks: {
    "RGPD": "GDPR", "ISO/IEC 27001": "ISO/IEC 27001", "NIS2": "NIS2", "DORA": "DORA",
    "PCI DSS": "PCI DSS", "HDS": "Healthcare data hosting (HDS)",
    "Exigences client": "Customer requirements", "Cyberassurance": "Cyber insurance",
    "Audit interne": "Internal audit", "Audit financier / SOX": "Financial audit / SOX",
    "NIST": "NIST", "Autre": "Other",
  },
  hygiene_questions: {
    h_domain_admins_limited: "Domain Admins membership is limited and reviewed.",
    h_admin_separate_accounts: "Admins use separate accounts for standard and privileged use.",
    h_dormant_accounts: "Dormant accounts are regularly identified and disabled.",
    h_offboarding: "Accounts of former employees and contractors are removed promptly.",
    h_service_owner: "Service accounts have a named owner.",
    h_service_no_privileges: "Service accounts do not carry excessive privileges.",
    h_technical_passwords_rotated: "Technical account passwords are rotated.",
    h_service_interactive: "Interactive logons for service accounts are blocked or monitored.",
    h_gpo_documented: "Critical GPOs are documented.",
    h_gpo_monitored: "GPO changes are monitored.",
    h_delegations_reviewed: "OU delegations are reviewed.",
    h_nested_groups: "Nested groups and indirect privileges are analysed.",
    h_shadow_admins: "Shadow admins are actively searched for.",
    h_adcs_audited: "ADCS templates and enrolment rights are audited.",
    h_legacy_protocols: "Legacy systems and protocols are identified.",
    h_mfa_entra: "MFA protects sensitive Entra ID roles.",
    h_hybrid_risks: "Hybrid AD / Entra risks are evaluated.",
    h_remediation_tracked: "Remediation is tracked over time.",
  },
  monitoring_questions: {
    m_alert_domain_admins: "Do you get an alert when someone is added to Domain Admins?",
    m_detect_short_elevation: "Can you detect a 20-minute privilege elevation?",
    m_who_deleted: "Can you identify who deleted an account?",
    m_before_after: "Can you see values before and after a critical change?",
    m_lockout_origin: "Can you identify the origin of a recurring account lockout?",
    m_failed_logins: "Do you monitor failed logins and repeated attempts?",
    m_offhours: "Do you monitor administrative activity outside business hours?",
    m_audit_policy: "Do you monitor audit-policy modifications?",
    m_account_creation: "Do you monitor account creations or re-activations?",
    m_state_in_time: "Can you reconstruct group membership at a past date?",
    m_admin_history: "Can you retrieve all actions performed by a given admin?",
    m_central_history: "Do you have a centralised history?",
    m_auditor_reports: "Can you quickly produce a report for an auditor?",
    m_soc: "Do you have a SOC or a person handling alerts?",
  },
};

export function translateRefData(rd, lang) {
  if (!rd || lang !== "en") return rd;
  const map = REFDATA_EN;
  const relabel = (arr, kind, keyProp = "id", labelProp = "label") =>
    (arr || []).map((it) => {
      if (typeof it === "string") {
        return map[kind]?.[it] || it;
      }
      const k = it[keyProp];
      const t = map[kind]?.[k];
      return t ? { ...it, [labelProp]: t } : it;
    });

  return {
    ...rd,
    objectives: relabel(rd.objectives, "objectives"),
    sectors: relabel(rd.sectors, "sectors"),
    sizes: (rd.sizes || []).map((s) => ({ ...s, label: map.sizes[s.id] || s.label })),
    criticity_levels: relabel(rd.criticity_levels, "criticity_levels"),
    downtime_costs: relabel(rd.downtime_costs, "downtime_costs"),
    deployment: relabel(rd.deployment, "deployment"),
    yes_no_unknown: relabel(rd.yes_no_unknown, "yes_no_unknown"),
    maturity_levels: relabel(rd.maturity_levels, "maturity_levels"),
    monitoring_levels: relabel(rd.monitoring_levels, "monitoring_levels"),
    frameworks: relabel(rd.frameworks, "frameworks"),
    hygiene_questions: (rd.hygiene_questions || []).map((q) => ({
      ...q, label: map.hygiene_questions[q.id] || q.label,
    })),
    monitoring_questions: (rd.monitoring_questions || []).map((q) => ({
      ...q, label: map.monitoring_questions[q.id] || q.label,
    })),
  };
}
